import React, { useState } from 'react';
import { Eye, EyeOff, MapPin, User } from 'lucide-react';

const PaymentForm = ({ paymentMethod, formData, onFormChange }) => {
  const [showCvv, setShowCvv] = useState(false);

  const updateFormData = (path, value) => {
    const newFormData = { ...formData };
    const pathArray = path?.split('.');
    let current = newFormData;
    
    for (let i = 0; i < pathArray?.length - 1; i++) {
      current = current?.[pathArray?.[i]];
    }
    current[pathArray[pathArray.length - 1]] = value;
    
    onFormChange(newFormData);
  };

  const formatCardNumber = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value?.replace(/\D/g, '');
    if (v?.length >= 2) {
      return `${v?.slice(0, 2)}/${v?.slice(2, 4)}`;
    }
    return v;
  };

  if (paymentMethod === 'payfast') {
    return (
      <div className="space-y-6">
        <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
          <div className="flex items-start space-x-3">
            <img
              src="/assets/payfast-logo.png"
              alt="PayFast"
              className="w-8 h-8 mt-1"
              onError={(e) => {
                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='6' fill='%23FF6B35'/%3E%3Ctext x='16' y='20' text-anchor='middle' fill='white' font-family='Arial' font-size='12' font-weight='bold'%3EPF%3C/text%3E%3C/svg%3E";
              }}
            />
            <div>
              <h4 className="text-sm font-medium text-orange-900">PayFast Payment</h4>
              <p className="text-sm text-orange-700 mt-1">
                You will be redirected to PayFast to complete your payment securely.
                PayFast supports all major South African banks and payment methods.
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Holder Name
            </label>
            <input
              type="text"
              value={formData?.cardName}
              onChange={(e) => updateFormData('cardName', e?.target?.value)}
              placeholder="Name on card"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <strong>Supported Payment Methods:</strong> Credit/Debit Cards, Internet Banking,
            Instant EFT, Bitcoin, Store Cards, and Mobile Payments.
          </p>
        </div>
      </div>
    );
  }

  if (paymentMethod === 'paystack') {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start space-x-3">
            <img
              src="/assets/paystack-logo.png"
              alt="Paystack"
              className="w-8 h-8 mt-1"
              onError={(e) => {
                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='6' fill='%2300C8FF'/%3E%3Ctext x='16' y='20' text-anchor='middle' fill='white' font-family='Arial' font-size='12' font-weight='bold'%3EPS%3C/text%3E%3C/svg%3E";
              }}
            />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Paystack Payment</h4>
              <p className="text-sm text-blue-700 mt-1">
                Secure payment processing with Paystack. Supports cards, bank transfers,
                and mobile money across Africa.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="+27 123 456 789"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <p className="text-sm text-green-800">
            <strong>Payment Options:</strong> Visa, Mastercard, Verve, Bank Transfer,
            USSD, QR Code, and Mobile Money.
          </p>
        </div>
      </div>
    );
  }

  // Card payment form
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Number
        </label>
        <input
          type="text"
          value={formData?.cardNumber}
          onChange={(e) => updateFormData('cardNumber', formatCardNumber(e?.target?.value))}
          placeholder="1234 5678 9012 3456"
          maxLength={19}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expiry Date
          </label>
          <input
            type="text"
            value={formData?.expiryDate}
            onChange={(e) => updateFormData('expiryDate', formatExpiryDate(e?.target?.value))}
            placeholder="MM/YY"
            maxLength={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CVV
          </label>
          <div className="relative">
            <input
              type={showCvv ? 'text' : 'password'}
              value={formData?.cvv}
              onChange={(e) => updateFormData('cvv', e?.target?.value?.replace(/\D/g, '')?.slice(0, 4))}
              placeholder="123"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowCvv(!showCvv)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showCvv ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <User className="inline h-4 w-4 mr-1" />
          Card Holder Name
        </label>
        <input
          type="text"
          value={formData?.cardName}
          onChange={(e) => updateFormData('cardName', e?.target?.value)}
          placeholder="Name as it appears on card"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-900 flex items-center">
          <MapPin className="h-4 w-4 mr-1" />
          Billing Address
        </h4>
        
        <div>
          <input
            type="text"
            value={formData?.billingAddress?.street}
            onChange={(e) => updateFormData('billingAddress.street', e?.target?.value)}
            placeholder="Street address"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            value={formData?.billingAddress?.city}
            onChange={(e) => updateFormData('billingAddress.city', e?.target?.value)}
            placeholder="City"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          <select
            value={formData?.billingAddress?.province}
            onChange={(e) => updateFormData('billingAddress.province', e?.target?.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Province</option>
            <option value="GP">Gauteng</option>
            <option value="WC">Western Cape</option>
            <option value="KZN">KwaZulu-Natal</option>
            <option value="EC">Eastern Cape</option>
            <option value="FS">Free State</option>
            <option value="MP">Mpumalanga</option>
            <option value="NW">North West</option>
            <option value="LP">Limpopo</option>
            <option value="NC">Northern Cape</option>
          </select>
        </div>

        <div>
          <input
            type="text"
            value={formData?.billingAddress?.postalCode}
            onChange={(e) => updateFormData('billingAddress.postalCode', e?.target?.value)}
            placeholder="Postal code"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;